<template>
  <div class='flex flex-col justify-center'>

    <div class='flex items-center mx-auto gap-2 cursor-pointer' @click='togglePartner'>
      <input
        type="checkbox"
        name="partner"
        id="partner"
        @click="togglePartner"
        :checked="partnerEnabled"
        @change="togglePartner"/>
      <p class='font-bold button-2'>Partner details</p>
    </div>
    <div class="flex flex-col md:grid mt-6 px-4 gap-6" :class="[{ 'md:grid-cols-2': partnerEnabled }, {'md:grid-cols-1': !partnerEnabled}]">
      <div class='flex flex-col col-span-1 w-full gap-6'>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Your age</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="How old are you today?" />
          </div>
          <BaseInput
            v-model:value="form.user.age"
            v-bind="formatAsNumber"
            type="text"
            placeholder="Age"
            name="user-age"
            :error-message="userErrors.age" />
        </div>

        <div class='flex flex-col sm:flex-row gap-6 w-full'>
          <div class='flex flex-col w-full'>
            <div class='flex flex-row gap-2 items-center pb-2'>
              <p class="button-2">Your salary (before tax)</p>
              <info-circle
                css-class="cfs-tooltip"
                fill="cfs-blue"
                message="The total cash salary including the tax you must pay, plus super received." />
            </div>
            <BaseInput
              v-model:value="form.user.income"
              v-bind="formatAsCurrency"
              type="currency"
              placeholder="0"
              name="user-salary"
              @input='getRecommendedTargetSalary()'
              :error-message="userErrors.income" />
          </div>
          <div class='flex flex-col w-full'>
            <div class='flex flex-row gap-2 items-center pb-2'>
              <p class="button-2">Your salary frequency</p>
            </div>
            <Dropdown
              :items='frequencyItems'
              :existing-item='null'
              :filterable='false'
              default-value='yearly'
              @selected='(val) => form.user.income_time_period = val.value'
              type=''/>
          </div>
        </div>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Your super balance</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="If you have more than one super account, enter the total amount of all your super accounts combined." />
          </div>
          <BaseInput
            v-model:value="form.user.current_super_balance"
            v-bind="formatAsCurrency"
            type="currency"
            placeholder="0"
            name="user-super-balance"
            :error-message="userErrors.current_super_balance" />
        </div>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Your employer contribution rate</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The % of your salary that your employer adds to your super." />
          </div>
          <BaseInput
            v-model:value="form.user.contribution_rate"
            type="number"
            placeholder="12%"
            name="user-esg"
            @input='getRecommendedTargetSalary()'
            :error-message="userErrors.contribution_rate" />
        </div>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Your retirement age</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The age you want to stop working." />
          </div>
          <BaseInput
            v-model:value="form.user.desired_retirement_age"
            v-bind="formatAsNumber"
            type="text"
            placeholder="67"
            name="user-retirement-age"
            :error-message="userErrors.desired_retirement_age" />
        </div>
      </div>
      <div v-if='partnerEnabled' class='flex flex-col col-span-1 w-full mt-6 sm:mt-0 gap-6'>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Partner's age</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="How old are you today?" />
          </div>
          <BaseInput
            v-model:value="form.partner.age"
            v-bind="formatAsNumber"
            type="text"
            placeholder="Age"
            name="partner-age"
            :error-message="partnerErrors.age" />
        </div>
        <div class='flex flex-col sm:flex-row gap-6 w-full'>
          <div class='flex flex-col w-full'>
            <div class='flex flex-row gap-2 items-center pb-2'>
              <p class="button-2">Partner's salary (before tax)</p>
              <info-circle
                css-class="cfs-tooltip"
                fill="cfs-blue"
                message="The total cash salary including the tax you must pay, plus super received." />
            </div>
            <BaseInput
              v-model:value="form.partner.income"
              v-bind="formatAsCurrency"
              type="currency"
              placeholder="0"
              name="partner-salary"
              @input='getRecommendedTargetSalary()'
              :error-message="partnerErrors.income" />
          </div>
          <div class='flex flex-col w-full'>
            <p class="button-2 pb-2">Partner's salary frequency</p>
            <Dropdown
              :items='frequencyItems'
              :existing-item='null'
              :filterable='false'
              default-value='yearly'
              @selected='(val) => form.partner.income_time_period = val.value'
              type=''/>
          </div>
        </div>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Partner's super balance</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="If you have more than one super account, enter the total amount of all your super accounts combined." />
          </div>
          <BaseInput
            v-model:value="form.partner.current_super_balance"
            v-bind="formatAsCurrency"
            type="currency"
            placeholder="0"
            name="partner-super-balance"
            :error-message="partnerErrors.current_super_balance" />
        </div>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Partner's employer contribution rate</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The % of your salary that your employer adds to your super." />
          </div>
          <BaseInput
            v-model:value="form.partner.contribution_rate"
            type="number"
            placeholder="12%"
            name="partner-esg"
            @input='getRecommendedTargetSalary()'
            :error-message="partnerErrors.contribution_rate" />
        </div>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Partner's retirement age</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The age you want to stop working." />
          </div>
          <BaseInput
            v-model:value="form.partner.desired_retirement_age"
            v-bind="formatAsNumber"
            type="text"
            placeholder="67"
            name="partner-retirement-age"
            :error-message="partnerErrors.desired_retirement_age" />
        </div>
      </div>
      <div class='flex flex-col col-span-full w-full gap-6'>
        <div>
          <div class='flex flex-row gap-2 items-center pb-2'>
            <p class="button-2">Target annual household retirement income (in today's dollars)</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="The yearly income you would like to live on when you retire. Most people enter a figure that's around 70% of their working life household income after tax." />
          </div>
          <BaseInput
            v-model:value="form.user.desired_retirement_income"
            v-bind="formatAsCurrency"
            type="currency"
            placeholder="0"
            name="household-target-income"
            :error-message="userErrors.desired_retirement_income" />
        </div>
        <div class='flex flex-row col-span-full w-full gap-6'>
          <div class='flex flex-row gap-2 items-center'>
            <p class="button-2">Do you own your home?</p>
            <info-circle
              css-class="cfs-tooltip"
              fill="cfs-blue"
              message="This may alter your age pension payment." />
          </div>
          <BaseRadioButtons
            name="hasHome"
            :items="[
              { label: 'Yes', value: true },
              { label: 'No', value: false }
            ]"
            :default-value="user.hasHome.value !== null ? { label: user.hasHome.value ? 'Yes' : 'No', value: user.hasHome.value } : null"
            class="flex-row! gap-6!"
            :error-message="userErrors.hasHome"
            @selected="(val) => { form.user.hasHome = val.value; form.partner.hasHome = val.value }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang='ts'>
import { ref, computed, watch, toRefs } from 'vue'
import BaseInput from '@/components/Inputs/BaseInput.vue'
import { InputItem } from '@/types/InputItem.ts'
import formatAsCurrency from '@/composables/formatAsCurrency'
import formatAsNumber from '@/composables/formatAsNumber'
import BaseRadioButtons from '@/components/Inputs/BaseRadioButtons.vue'
import { useRetirementCalculatorStore } from '@/store/pinia/RetirementCalculatorStore'
import { useRetirementCalculatorErrorStore } from '@/store/pinia/RetirementCalculatorErrorStore'
import Dropdown from '@/components/BasicInputs/Dropdown.vue'
import RetCalcService from '@/services/RetCalcService.ts'
import { useDebounceFn } from '@vueuse/core'
import InfoCircle from '@/components/InfoCircle/InfoCircle.vue'

// Strict typing for salary frequency
type SalaryFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'yearly'

// Interface with field names matching CFS RetirementData API
interface FormDetails {
  age: number | null
  income: number | null // Changed from salary to match API
  income_time_period: SalaryFrequency | null // Changed from frequency to match API
  current_super_balance: number | null // Changed from superBalance to match API
  contribution_rate: number | null // Changed from esg to match API
  desired_retirement_income: number | null // Changed from targetIncome to match API
  desired_retirement_age: number | null // Changed from retirementAge to match API
  hasHome: boolean | null // Changed from ownHome to match API
}

const frequencyItems: InputItem<SalaryFrequency>[] = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Fortnightly', value: 'fortnightly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' }
]

// Store setup
const retirementCalculatorStore = useRetirementCalculatorStore()
const errorStore = useRetirementCalculatorErrorStore()

const user = toRefs(retirementCalculatorStore.getUserData)
const partner = toRefs(retirementCalculatorStore.getPartnerData)

// Partner form state from store
const partnerEnabled = computed(() => retirementCalculatorStore.getEnablePartnerForm)

// Error state from error store
const userErrors = computed(() => errorStore.getUserErrors)
const partnerErrors = computed(() => errorStore.getPartnerErrors)

// Toggle partner form method
const togglePartner = () => {
  retirementCalculatorStore.togglePartnerForm()
}



const getRecommendedTargetSalary = useDebounceFn(async () => {
  const calculateAnnualIncome = (person: FormDetails) => {
    if (!person.income) return 0
    switch (person.income_time_period) {
      case 'weekly':
        return person.income * 52
      case 'fortnightly':
        return person.income * 26
      case 'monthly':
        return person.income * 12
      case 'yearly':
        return person.income ?? 0
      default:
        return person.income ?? 0
    }
  }

  // Calculate combined household net income
  let totalHouseholdNetIncome = 0

  // Get user net income
  if (form.value.user.income && form.value.user.income > 0) {
    const userPayload = {
      gas: calculateAnnualIncome(form.value.user),
      desRetInc: form.value.user.contribution_rate || 12
    }
    const userRes = await RetCalcService.getNetIncome(userPayload)
    totalHouseholdNetIncome += userRes.data.netSalaryPerAnnum
  }

  // Get partner net income if partner is enabled and has income
  if (partnerEnabled.value && form.value.partner.income && form.value.partner.income > 0) {
    const partnerPayload = {
      gas: calculateAnnualIncome(form.value.partner),
      desRetInc: form.value.partner.contribution_rate || 12
    }
    const partnerRes = await RetCalcService.getNetIncome(partnerPayload)
    totalHouseholdNetIncome += partnerRes.data.netSalaryPerAnnum
  }

  // Calculate household target retirement income (70% of combined net income)
  form.value.user.desired_retirement_income = totalHouseholdNetIncome > 0
    ? Math.round(totalHouseholdNetIncome * 0.7)
    : 50000
}, 500)

// Form with CFS-aligned field names
const form = ref<{user: FormDetails, partner: FormDetails}>({
  user: {
    age: user.age.value,
    income: user.income.value,
    income_time_period: frequencyItems.find((item) => user.income_time_period.value === item.value) ?? null,
    current_super_balance: user.current_super_balance.value,
    contribution_rate: user.contribution_rate.value,
    desired_retirement_income: user.desired_retirement_income.value,
    desired_retirement_age: user.desired_retirement_age.value,
    hasHome: user.hasHome.value
  },
  partner: {
    age: partner.age.value,
    income: partner.income.value,
    income_time_period: frequencyItems.find((item) => partner.income_time_period.value === item.value) ?? null,
    current_super_balance: partner.current_super_balance.value,
    contribution_rate: partner.contribution_rate.value,
    desired_retirement_income: partner.desired_retirement_income.value,
    desired_retirement_age: partner.desired_retirement_age.value,
    hasHome: partner.hasHome.value
  }
})

// Watch form changes and sync with store
watch(() => form.value.user, (newUserData) => {
  Object.keys(newUserData).forEach(key => {
    retirementCalculatorStore.updateUserDataKeyPair({
      field: key,
      value: newUserData[key as keyof FormDetails]
    })
  })
}, { deep: true })

watch(() => form.value.partner, (newPartnerData) => {
  Object.keys(newPartnerData).forEach(key => {
    retirementCalculatorStore.updatePartnerDataKeyPair({
      field: key,
      value: newPartnerData[key as keyof FormDetails]
    })
  })
}, { deep: true })
</script>
